// src/components/Home.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar, Container, Nav, Button, Table, Card, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const getApplicationFee = (post, category) => {
  const normalizedPost = post.trim().toLowerCase();
  const normalizedCategory = category.trim().toUpperCase();

  if (normalizedCategory === 'ST') {
    if (normalizedPost === 'sweeper') return 400;
    if (['fireman driver', 'fireman', 'fireman ministerial'].includes(normalizedPost)) return 500;
  }

  if (normalizedCategory === 'EWS' && normalizedPost === 'fireman') return 600;

  return null;
};

const HomeNew = () => {
  const navigate = useNavigate();

  // Dashboard State
  const [forms, setForms] = useState([]);
  const [user, setUser] = useState("");
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Apply Modal State
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [availablePosts, setAvailablePosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState("");
  const [userData, setUserData] = useState({ name: "", email: "", district: "", category: "" });
  const feeAmount = availablePosts.includes(selectedPost)
    ? getApplicationFee(selectedPost, userData.category)
    : null;

  // --- Dashboard Logic --- //

  useEffect(() => {
    axios.get('/api/getFormsList')
      .then(response => {
        if (response.data) {
          if (response.data.message) {
            toast.error(response.data.message);
            navigate("/");
            return;
          }
          if (Array.isArray(response.data)) {
            setForms(response.data);
            if (response.data[0]) {
              setUser(response.data[0].Phone);
            } else {
              setUser(response.data.Phone);
              setShowWelcomeModal(true);
            }
          } else {
            setUser(response.data.Phone);
            setShowWelcomeModal(true);
          }
        }
      })
      .catch(error => {
        console.error('Error fetching forms:', error);
        navigate("/");
      });
  }, [navigate]);

  const handleExit = () => {
    navigate("/");
  };

  // --- Apply Modal & Payment Logic --- //

  const handleOpenApplyModal = async () => {
    setShowWelcomeModal(false);
    setShowApplyModal(true);
    setIsLoadingPosts(true);
    setSelectedPost("");
    setAvailablePosts([]);

    try {
      const response = await axios.get('/api/getPosts');

      if (response.data?.message) {
        toast.error(response.data.message);
        setShowApplyModal(false);
        return;
      }

      // Read posts directly from the updated API response array
      const posts = response.data.Posts || [];

      setAvailablePosts(posts);
      setSelectedPost(posts.length === 1 ? posts[0] : "");

      setUserData({
        name: response.data.name || "Name",
        email: response.data.email || "Email",
        district: response.data.district || "",
        category: response.data.category || ""
      });

      if (posts.length === 0) {
        toast.info("Not eligible for any post. Check your details.");
      }
    } catch (error) {
      console.error('Error fetching eligible posts:', error);
      toast.error("Failed to load eligible posts.");
      setShowApplyModal(false);
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const handleCloseApplyModal = () => {
    setShowApplyModal(false);
    setSelectedPost("");
  };

  const postToPayU = (data) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://secure.payu.in/_payment";
    //form.action = "https://test.payu.in/_payment";

    Object.entries(data).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    // Only submit a post returned by the eligibility endpoint.
    if (!selectedPost || !availablePosts.includes(selectedPost)) {
      toast.error("Please select a post before proceeding.");
      return;
    }

    if (feeAmount === null) {
      toast.error("A fee is not available for this post and category.");
      return;
    }

    try {
      // Preserve the API's post array format with one selected post.
      const response = await axios.post('/api/newform', {
        post: [selectedPost],
        amount: feeAmount,
        district: userData.district
      });

      const response2 = await axios.post('/api/payugetTransString', {
        amount: feeAmount,
        applicationNumber: response.data,
        name: userData.name,
        email: userData.email
      });

      postToPayU(response2.data);
    } catch (error) {
      toast.error("An error occurred while initiating payment.");
      console.error(error);
    }
  };

  return (
    <div style={{ backgroundColor: '#dee0d7', minHeight: '100vh' }} >

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px',
          backgroundColor: '#06213f',
        }}
      >
        <img
          src="/logo_0.png"
          alt="Header Image"
          style={{
            maxWidth: '100%',
            height: 'auto',
            maxHeight: '100px',
            objectFit: 'contain',
            backgroundColor: '#06213f',
          }}
        />
      </div>

      <Navbar collapseOnSelect expand="lg" variant="dark" style={{ backgroundColor: '#06213f' }}>
        <Container>
          <Navbar.Brand href="/home">Ladakh Fire Services Recruitment 2026</Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse className="justify-content-end">
            <Nav className="me-auto">
              <Nav.Link active>Application Forms</Nav.Link>
              <Nav.Link onClick={() => navigate("/profile")}>Modify Profile</Nav.Link>
            </Nav>
            <Navbar.Text>Signed in as: {user}</Navbar.Text>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Main Dashboard */}
      <Container className="d-flex justify-content-center py-5">
        <Card className="shadow-lg border-0 w-100" style={{ maxWidth: "1200px" }}>
          <Card.Body className="p-4 p-md-5">
            <h2 className="text-center fw-bold mb-4">Submitted Applications</h2>

            {forms.length === 0 ? (
              <Alert variant="info" className="text-center fw-bold">
                No Applications Submitted yet. Click on Apply below.
              </Alert>
            ) : (
              <div className="table-responsive mt-4">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Application ID</th>
                      <th>Post</th>
                      <th>Fee Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forms.map((form, index) => (
                      <tr key={index}>
                        <td>
                          <Link to={`/form-details/${form.applicationNumber}`} className="fw-bold text-decoration-none">
                            {form.applicationNumber}
                          </Link>
                        </td>
                        <td>{form.Posts}</td>
                        <td>₹{form.amount}</td>
                        <td>
                          {form.status === 1 ? (
                            <span className="badge bg-success">Payment Successful</span>
                          ) : form.status === 2 ? (
                            <span className="badge bg-warning text-dark">Payment Pending. Wait for clearance</span>
                          ) : (
                            <span className="badge bg-danger">Payment Failed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <div className="d-flex justify-content-center gap-3 mt-5">
              <Button onClick={handleOpenApplyModal} style={{ backgroundColor: '#06213f', borderColor: '#06213f' }} size="lg">
                Apply for Post
              </Button>
              <Button onClick={handleExit} variant="outline-danger" size="lg">
                LOGOUT
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Welcome Modal */}
      <Modal show={showWelcomeModal} onHide={() => setShowWelcomeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Welcome to Ladakh Fire Services Application Portal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your account has been created successfully.</p>
          <p>To apply for a post, click on the <b>Apply for Post</b> button.</p>
          <p>Your submitted applications will appear on your dashboard.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWelcomeModal(false)}>Close</Button>
          <Button style={{ backgroundColor: '#06213f' }} onClick={handleOpenApplyModal}>Apply for Post</Button>
        </Modal.Footer>
      </Modal>

      {/* Apply / Payment Modal */}
      <Modal show={showApplyModal} onHide={handleCloseApplyModal} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Submit Form</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {isLoadingPosts ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Checking your eligibility...</p>
            </div>
          ) : availablePosts.length > 0 ? (
            <Form onSubmit={handlePaymentSubmit}>

              {/* 1. Post Selection Section */}
              <div className="mb-4">
                <Alert variant="info">
                  <strong>Requirement:</strong> Choose one eligible post to apply for at a time.
                </Alert>

                <div className="d-flex flex-column gap-3">
                    <div className="p-3 rounded shadow-sm m-0" style={{ backgroundColor: '#f4f6f9', border: '1px solid #dee2e6' }}>
                      <Form.Group controlId="selectedPost">
                        <Form.Label className="fw-bold" style={{ color: '#06213f' }}>Post:</Form.Label>
                        <Form.Select
                          value={selectedPost}
                          onChange={(e) => setSelectedPost(e.target.value)}
                          required
                        >
                          <option value="">-- Select Post --</option>
                          {availablePosts.map((post) => (
                            <option
                              key={post}
                              value={post}
                            >
                              {post}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                </div>
              </div>

              {/* 2. Payment Summary Section */}
              {feeAmount !== null && (
              <div className="mt-4 p-4 rounded shadow-sm" style={{ backgroundColor: '#f8f9fa', borderLeft: '5px solid #06213f' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="fw-bold mb-0" style={{ color: '#06213f' }}>Total Payable Amount</h5>
                    <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                      Fee for {selectedPost} ({userData.category})
                    </span>
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.75rem', color: '#06213f' }}>
                    ₹{feeAmount}
                  </div>
                </div>

                <div className="p-2 rounded" style={{ backgroundColor: '#fff5f5', border: '1px solid #ffe3e3' }}>
                  <p className="text-danger mb-0" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    <strong>* Note:</strong> Payment is non-refundable under any circumstances.
                  </p>
                </div>
              </div>
              )}

              {selectedPost && feeAmount === null && (
                <Alert variant="warning">A fee is not available for this post and category.</Alert>
              )}

              {/* 3. Action Buttons Section */}
              <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                <Button
                  variant="light"
                  className="border fw-bold text-muted px-4 py-2"
                  onClick={handleCloseApplyModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="fw-bold px-4 py-2 shadow-sm"
                  style={{ backgroundColor: '#06213f', borderColor: '#06213f' }}
                  disabled={!selectedPost || !availablePosts.includes(selectedPost) || feeAmount === null}
                >
                  Make Payment <span className="ms-2">→</span>
                </Button>
              </div>

            </Form>
          ) : (
            <Alert variant="warning" className="text-center fw-bold">
              You have already successfully applied for all eligible posts.
            </Alert>
          )}
        </Modal.Body>
      </Modal>

      <ToastContainer position="bottom-center" autoClose={5000} />
    </div>
  );
};

export default HomeNew;
